import { getToken } from '@/modules/huyu-framework/backend/utils/auth'

/**
 * 全局唯一 TTS 播放内核（时间轴版）
 *
 * 与旧实现（api/huyuAI/speech.js 中的队列调度）的区别：
 * 1. 分段解码后常驻 segments 数组，不再出队丢弃 → 可回拖、可重播
 * 2. 维护统一时间轴（每段 offset + duration）→ 进度条按秒显示、可 seek
 * 3. seek 只允许落在已合成区间（<= synthesizedDuration），保留边合边播的低首字延迟
 * 4. 每段携带后端下发的段文本（SSE 事件 text 字段）→ 供句级高亮使用
 * 5. 以 messageId 标识朗读对象（不再用文本内容比对），切换对话不会串状态
 */

const SSE_URL = '/huyuAI/speech/tts/stream'

let audioCtx = null
let seq = 0                 // 每次 play/stop 递增，用于丢弃过期异步结果
let abortCtrl = null

let phase = 'idle'          // idle | synthesizing | playing | paused
let waiting = false         // 已播到已合成末尾，等待后续分段
let currentMessageId = ''
let currentText = ''
let currentEngine = 'local'
let currentVoice = ''

let segments = []           // { index, text, buffer, offset, duration }
let streamEnded = false

let scheduled = []          // { source, segIndex }
let nextSegIdx = 0          // 下一个待调度段下标
let nextStartCtxTime = 0    // 下一段在 AudioContext 时间轴上的起播时刻
let anchorCtxTime = 0       // 锚点：ctx 时间
let anchorOffset = 0        // 锚点：对应的朗读时间轴位置（秒）
let pausedOffset = 0        // 非播放态下的时间轴位置（秒）
let progressTimer = null

function getCtx() {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)()
  return audioCtx
}

function synthesizedDuration() {
  if (segments.length === 0) return 0
  const last = segments[segments.length - 1]
  return last.offset + last.duration
}

function computeCurrentTime() {
  if (phase === 'playing' && !waiting) {
    const t = anchorOffset + (getCtx().currentTime - anchorCtxTime)
    if (t < 0) return 0
    const max = synthesizedDuration()
    return t > max ? max : t
  }
  return pausedOffset
}

// 时间轴位置 → 段下标（-1 表示该位置尚未合成）
function locate(offset) {
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]
    if (offset < seg.offset + seg.duration - 0.001) {
      return { segIdx: i, inner: Math.max(0, offset - seg.offset) }
    }
  }
  return { segIdx: -1, inner: 0 }
}

function segIndexAt(offset) {
  const { segIdx } = locate(offset)
  if (segIdx >= 0) return segIdx
  return segments.length > 0 ? segments.length - 1 : -1
}

// ==================== 状态广播 ====================
const listeners = new Set()

export function onTtsState(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

export function getTtsState() {
  const currentTime = computeCurrentTime()
  const segIdx = segIndexAt(currentTime)
  return {
    messageId: currentMessageId,
    text: currentText,
    engine: currentEngine,
    voice: currentVoice,
    phase,
    waiting,
    currentTime,
    duration: synthesizedDuration(),
    synthesizedDuration: synthesizedDuration(),
    streamEnded,
    segIndex: segIdx,
    segText: segIdx >= 0 ? segments[segIdx].text : '',
    segTotal: segments.length
  }
}

function emit() {
  const state = getTtsState()
  listeners.forEach(fn => {
    try { fn(state) } catch (e) { /* ignore */ }
  })
}

function startProgressTimer() {
  if (progressTimer) return
  progressTimer = setInterval(() => {
    if (phase === 'playing' && !waiting) emit()
  }, 150)
}

function stopProgressTimer() {
  if (progressTimer) { clearInterval(progressTimer); progressTimer = null }
}

// ==================== 播放调度 ====================
function cancelScheduled() {
  scheduled.forEach(item => {
    try { item.source.onended = null; item.source.stop() } catch (e) { /* ignore */ }
  })
  scheduled = []
}

// 从 nextSegIdx 起把所有已解码段接续排到时间轴上（firstInner 仅作用于第一段：seek 后的段内偏移）
function scheduleAvailable(firstInner = 0) {
  const ctx = getCtx()
  let inner = firstInner
  const mySeq = seq
  while (nextSegIdx < segments.length) {
    const seg = segments[nextSegIdx]
    const source = ctx.createBufferSource()
    source.buffer = seg.buffer
    source.connect(ctx.destination)
    if (nextStartCtxTime < ctx.currentTime) nextStartCtxTime = ctx.currentTime + 0.02
    source.start(nextStartCtxTime, inner)
    nextStartCtxTime += (seg.duration - inner)
    const segIndex = nextSegIdx
    scheduled.push({ source, segIndex })
    source.onended = () => onSourceEnded(mySeq, source)
    inner = 0
    nextSegIdx++
  }
}

function onSourceEnded(mySeq, source) {
  if (mySeq !== seq) return
  scheduled = scheduled.filter(item => item.source !== source)
  if (scheduled.length > 0) return
  if (streamEnded && nextSegIdx >= segments.length) {
    finish()
    return
  }
  // 已播到已合成末尾，等待后续分段到达后自动续播
  waiting = true
  pausedOffset = synthesizedDuration()
  nextStartCtxTime = 0
  emit()
}

function startPlaybackAt(offset) {
  cancelScheduled()
  const ctx = getCtx()
  if (ctx.state === 'suspended') { ctx.resume().catch(() => { /* ignore */ }) }
  const { segIdx, inner } = locate(offset)
  if (segIdx < 0) {
    // 目标位置尚未合成：挂起等待
    pausedOffset = offset
    waiting = true
    phase = 'playing'
    emit()
    return
  }
  nextSegIdx = segIdx
  nextStartCtxTime = ctx.currentTime + 0.05
  anchorCtxTime = nextStartCtxTime
  anchorOffset = segments[segIdx].offset + inner
  phase = 'playing'
  waiting = false
  scheduleAvailable(inner)
  startProgressTimer()
  emit()
}

function finish() {
  stopProgressTimer()
  cancelScheduled()
  if (abortCtrl) { abortCtrl.abort(); abortCtrl = null }
  phase = 'idle'
  waiting = false
  pausedOffset = 0
  segments = []
  nextSegIdx = 0
  nextStartCtxTime = 0
  streamEnded = false
  currentMessageId = ''
  currentText = ''
  emit()
}

// ==================== 对外操作 ====================

/** 彻底停止并释放（幂等） */
export function stop() {
  seq++
  stopProgressTimer()
  cancelScheduled()
  if (abortCtrl) { abortCtrl.abort(); abortCtrl = null }
  const wasActive = phase !== 'idle'
  phase = 'idle'
  waiting = false
  segments = []
  nextSegIdx = 0
  nextStartCtxTime = 0
  anchorCtxTime = 0
  anchorOffset = 0
  pausedOffset = 0
  streamEnded = false
  currentMessageId = ''
  currentText = ''
  if (wasActive) emit()
}

/** 暂停（保留已合成段与进度，SSE 继续接收） */
export function pause() {
  if (phase !== 'playing') return
  pausedOffset = computeCurrentTime()
  cancelScheduled()
  stopProgressTimer()
  phase = 'paused'
  waiting = false
  nextStartCtxTime = 0
  emit()
}

/** 从暂停位置继续 */
export function resume() {
  if (phase !== 'paused') return
  startPlaybackAt(pausedOffset)
}

/** 跳转到指定时间（秒）。仅允许落在已合成区间内 */
export function seek(time) {
  const max = synthesizedDuration()
  if (max <= 0) return
  let t = Number(time)
  if (!isFinite(t) || t < 0) t = 0
  if (t > max) t = max
  if (phase === 'playing') {
    startPlaybackAt(t)
  } else if (phase === 'paused' || phase === 'synthesizing') {
    pausedOffset = t
    emit()
  }
}

/**
 * 开始朗读（全局互斥：自动中断上一条）
 * @param {string} messageId 消息稳定 ID，作为唯一标识
 * @param {string} text 待朗读文本（原始 Markdown，由后端清洗）
 * @param {object} options { engine: 'local'|'edgetts', voice }
 */
export async function play(messageId, text, options = {}) {
  const content = (text || '').trim()
  if (!content) return
  stop()
  const mySeq = ++seq
  currentMessageId = messageId || ''
  currentText = content
  currentEngine = options.engine || 'local'
  currentVoice = options.voice || ''
  phase = 'synthesizing'
  waiting = false
  pausedOffset = 0
  emit()
  try {
    await streamSse(mySeq, content, currentEngine, currentVoice)
  } catch (e) {
    if (mySeq === seq) { stop(); throw e }
  }
}

/** 同一条消息再点击则停止，否则开始朗读该条 */
export function toggle(messageId, text, options = {}) {
  if (phase !== 'idle' && currentMessageId && currentMessageId === messageId) {
    stop()
    return Promise.resolve(false)
  }
  return play(messageId, text, options).then(() => true)
}

export function isPlaying(messageId) {
  if (phase === 'idle') return false
  if (!messageId) return true
  return currentMessageId === messageId
}

// ==================== SSE 拉流 ====================
function streamSse(mySeq, text, engine, voice) {
  return new Promise((resolve, reject) => {
    const controller = new AbortController()
    abortCtrl = controller
    fetch(import.meta.env.VITE_APP_BASE_API + SSE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Authorization': 'Bearer ' + getToken()
      },
      body: JSON.stringify({ text, engine, voice }),
      signal: controller.signal
    }).then(async (response) => {
      if (!response.ok) {
        let detail = ''
        try {
          const parsed = JSON.parse(await response.text())
          detail = parsed.msg || parsed.message || ''
        } catch (e) { /* ignore */ }
        throw new Error('语音合成失败: ' + (detail || 'HTTP ' + response.status))
      }
      if (!response.body) throw new Error('语音合成失败: 浏览器不支持流式响应')
      const reader = response.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buf = ''
      while (true) {
        const { value, done } = await reader.read()
        if (done) break
        if (mySeq !== seq) return
        buf += decoder.decode(value, { stream: true })
        let sep
        while ((sep = buf.indexOf('\n\n')) !== -1) {
          const raw = buf.slice(0, sep)
          buf = buf.slice(sep + 2)
          const line = raw.split('\n').find(l => l.startsWith('data:'))
          if (!line) continue
          const data = line.slice(5).trim()
          if (data === '[DONE]') { streamEnded = true; continue }
          try {
            const evt = JSON.parse(data)
            if (evt && evt.base64) {
              await appendSegment(mySeq, evt.index || 0, evt.text || '', evt.base64)
            }
          } catch (e) { /* ignore 非 JSON 事件 */ }
        }
      }
      if (mySeq !== seq) return
      streamEnded = true
      await waitAllPlayed(mySeq)
      resolve()
    }).catch((err) => {
      if (err && err.name === 'AbortError') { resolve(); return }
      reject(err)
    })
  })
}

async function appendSegment(mySeq, index, text, base64) {
  if (mySeq !== seq) return
  const ctx = getCtx()
  if (ctx.state === 'suspended') { try { await ctx.resume() } catch (e) { /* ignore */ } }
  let buffer
  try {
    buffer = await ctx.decodeAudioData(base64ToArrayBuffer(base64))
  } catch (e) {
    return // 单段解码失败忽略该段，不阻塞整体
  }
  if (mySeq !== seq) return
  const offset = synthesizedDuration()
  segments.push({ index, text, buffer, offset, duration: buffer.duration })
  if (phase === 'synthesizing') {
    // 首段到达即起播
    startPlaybackAt(0)
    return
  }
  if (phase === 'playing') {
    if (waiting) {
      // 之前播到末尾在等，续播新段
      startPlaybackAt(pausedOffset)
    } else {
      scheduleAvailable()
      emit()
    }
    return
  }
  emit() // paused：仅累积，不自动起播
}

function waitAllPlayed(mySeq) {
  return new Promise((resolve) => {
    const check = () => {
      if (mySeq !== seq) { resolve(); return }
      if (phase === 'idle') { resolve(); return }
      if (streamEnded && nextSegIdx >= segments.length && scheduled.length === 0 && phase !== 'paused') {
        resolve()
        return
      }
      setTimeout(check, 120)
    }
    check()
  })
}

function base64ToArrayBuffer(b64) {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes.buffer
}

export default { play, toggle, stop, pause, resume, seek, isPlaying, getTtsState, onTtsState }
